import type { ShallowDehydrateObject } from 'kysely'
import { z } from 'zod'

import type {
  ServiceContract,
  ServiceContractPayment,
  ServiceContractInitializer,
  ServiceContractPaymentId,
  EmbeddedLoggerLastTransmission,
  ServiceContractItemInitializer,
  ServiceContractItem,
  ServiceContractItemId,
  LoggerActivationsView,
} from '@beaded/models'
import {
  serviceContractInitializer,
  productServiceHistoryId,
  serviceContractMutator,
} from '@beaded/models'

export type ExtendedServiceContractInitializer = ServiceContractInitializer & {
  items: Array<
    Omit<
      ServiceContractItemInitializer,
      | 'serviceContractId'
      | 'serviceDateRevenueStartedAt'
      | 'serviceDateRevenueEndedAt'
    > & {
      serviceDateRevenueStartedAt?: Date | null
      serviceDateRevenueEndedAt?: Date | null
    }
  >
}

export type ExtendedServiceContractPayment = Array<
  ShallowDehydrateObject<
    Omit<
      ServiceContractPayment,
      | 'id'
      | 'notes'
      | 'createdAt'
      | 'createdBy'
      | 'updatedAt'
      | 'updatedBy'
      | 'deletedAt'
      | 'deletedBy'
    >
  > & {
    paymentId: ServiceContractPaymentId
    paymentNotes: string | null
  }
>

export type ExtendedServiceContract = Omit<
  ServiceContract,
  'deletedAt' | 'deletedBy' | 'createdBy' | 'updatedBy'
> & {
  projectName: string | null
  organizationName: string | null
  items: ExtendedServiceContractItem[]
  contractPayments: ExtendedServiceContractPayment
  totalDueInCents: number | null
  createdByUser: string | null
  updatedByUser: string | null

  /** Loggers currently activated on this contract */

  lastTransmission: EmbeddedLoggerLastTransmission
}

// Embedded via kysely's jsonArrayFrom: postgres json_agg serializes timestamps
// to ISO strings and node-postgres does not revive them, so the date columns
// arrive as strings. ShallowDehydrateObject is the same transformation the
// query builder applies, so this type cannot drift from the real row shape.
export type ExtendedServiceContractItem = ShallowDehydrateObject<
  Omit<
    ServiceContractItem,
    | 'deletedAt'
    | 'deletedBy'
    | 'createdBy'
    | 'createdAt'
    | 'updatedBy'
    | 'updatedAt'
    | 'id'
    | 'serviceContractItemNotes'
    | 'serviceDateRevenueStartedAt'
    | 'serviceDateRevenueEndedAt'
    | 'notes'
  >
> & {
  serviceContractItemId: ServiceContractItemId
  serviceContractItemNotes: string
  // Omitted from the base type above and re-declared here; like the rest of
  // this json-embedded row they arrive as ISO strings, not Date objects.
  serviceDateRevenueStartedAt?: string | null
  serviceDateRevenueEndedAt?: string | null
  loggerActivations: ShallowDehydrateObject<LoggerActivationsView>[]
}

const contractItemFields = z.object({
  notes: z.string().nullable(),
  serviceEndDate: z.coerce.date(),
  serviceStartDate: z.coerce.date(),
  serviceExpiredAt: z.coerce.date(),
  totalPriceInCents: z.number().int(),

  // NOTE: These previously used z.preprocess() to coerce empty-string/falsy
  // inputs to undefined, but express-zod-api v28's OpenAPI generator serializes
  // preprocess/transform-wrapped fields as an invalid `format: undefined
  // (preprocessed)`. Since this is a request-input schema, callers must send
  // undefined/null (or omit the field) rather than '' for an absent trial
  // period -- an empty string will fail date coercion. Matches the sibling
  // serviceDateRevenue*/invoiceDate fields.
  trialPeriodEnd: z.coerce.date().optional().nullable().optional(),
  trialPeriodStart: z.coerce.date().optional().nullable().optional(),

  productServiceHistoryId: productServiceHistoryId,
  serviceName: z.string(),
  servicePriceInCents: z.number(),
  serviceQuantity: z.number().int().nullable().optional(),
  serviceDuration: z.number().int().nullable().optional(),
  serviceDurationType: z.string().nullable().optional(),
  serviceUnitType: z.string().nullable().optional(),
  internal: z.boolean().optional(),
  sponsored: z.boolean().optional(),
  rental: z.boolean().optional(),
  serviceDateRevenueStartedAt: z.coerce.date().optional().nullable().optional(),
  serviceDateRevenueEndedAt: z.coerce.date().optional().nullable().optional(),
})

export const extendedContractInitializer = z.object({
  ...(serviceContractInitializer as unknown as z.ZodObject<any>).shape,
  invoiceDate: z.coerce.date().optional().nullable().optional(),
  items: z.array(contractItemFields),
}) as unknown as z.Schema<ExtendedServiceContractInitializer>

export const extendedContractMutator = z.object({
  ...(serviceContractMutator as unknown as z.ZodObject<any>).shape,
  invoiceDate: z.coerce.date().optional().nullable().optional(),
  items: z.array(contractItemFields),
}) as unknown as z.Schema<ExtendedServiceContractInitializer>
